import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import DescriptionIcon from '@mui/icons-material/Description';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import PlaceIcon from '@mui/icons-material/Place';
import EventIcon from '@mui/icons-material/Event';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useTranslation } from 'react-i18next';

import { MissingTravelField } from '@bandido/shared/types';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';

import { useCopilotFacets } from '../queries';

// Identificadores de facet (language-neutral); el label se traduce vía i18n.
const FACET_ICON: Record<string, JSX.Element> = {
  weather: <WbSunnyIcon fontSize="small" />,
  documentation: <DescriptionIcon fontSize="small" />,
  money: <CurrencyExchangeIcon fontSize="small" />,
  packages: <Inventory2Icon fontSize="small" />,
  highlights: <PlaceIcon fontSize="small" />,
  events: <EventIcon fontSize="small" />,
  risk: <WarningAmberIcon fontSize="small" />,
  socialProof: <StarBorderIcon fontSize="small" />,
};

interface PopoverState {
  anchor: HTMLElement;
  facet: string;
  fields: MissingTravelField[];
  values: Record<string, string>;
}

interface Props {
  caseId: number;
  onRequestFacet: (
    facet: string,
    overrides?: Record<string, string>,
  ) => Promise<{ missingFields: MissingTravelField[]; found: boolean; throttled: boolean }>;
}

export function TravelFacetBar({ caseId, onRequestFacet }: Props) {
  const { data: facets = [] } = useCopilotFacets(caseId);
  const { addToast } = useSnackBar();
  const { t } = useTranslation();
  const [loadingFacet, setLoadingFacet] = useState<string | null>(null);
  const [popover, setPopover] = useState<PopoverState | null>(null);

  const facetLabel = (facet: string) =>
    t(`copilot.travelIntelligence.facets.${facet}`, facet);

  if (!facets.length) return null;

  const fire = async (
    facet: string,
    anchor: HTMLElement,
    overrides?: Record<string, string>,
  ) => {
    setLoadingFacet(facet);
    try {
      const { missingFields, found, throttled } = await onRequestFacet(
        facet,
        overrides,
      );
      if (throttled) {
        addToast({
          message: t('copilot.travelIntelligence.bar.throttled'),
          color: 'warning',
        });
        return;
      }
      if (missingFields.length) {
        // Sembramos los defaults (ej. el select de categoría) para que el botón
        // "Consultar" arranque habilitado.
        const values: Record<string, string> = { ...(overrides ?? {}) };
        for (const f of missingFields) {
          if (f.defaultValue && !values[f.field]) values[f.field] = f.defaultValue;
        }
        setPopover({ anchor, facet, fields: missingFields, values });
      } else {
        setPopover(null);
        if (!found) {
          addToast({
            message: t('copilot.travelIntelligence.bar.noResults', {
              facet: facetLabel(facet),
            }),
            color: 'info',
          });
        }
      }
    } catch {
      addToast({
        message: t('copilot.travelIntelligence.bar.requestError'),
        color: 'error',
      });
    } finally {
      setLoadingFacet(null);
    }
  };

  return (
    <>
      <Stack
        direction="row"
        spacing={0.5}
        flexWrap="wrap"
        useFlexGap
        justifyContent="center"
        sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}
      >
        {facets.map((facet) => {
          const icon = FACET_ICON[facet];
          if (!icon) return null;
          return (
            <Tooltip key={facet} title={facetLabel(facet)}>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={loadingFacet === facet}
                  onClick={(e) => fire(facet, e.currentTarget)}
                >
                  {loadingFacet === facet ? (
                    <CircularProgress size={16} />
                  ) : (
                    icon
                  )}
                </IconButton>
              </span>
            </Tooltip>
          );
        })}
      </Stack>

      <Popover
        open={Boolean(popover)}
        anchorEl={popover?.anchor}
        onClose={() => setPopover(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {popover && (
          <Box
            sx={{
              p: 2,
              width: 260,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {t('copilot.travelIntelligence.bar.missing', {
                facet: facetLabel(popover.facet),
              })}
            </Typography>
            {popover.fields.map((f) => (
              <TextField
                key={f.field}
                size="small"
                select={f.type === 'select'}
                type={f.type === 'date' ? 'date' : 'text'}
                label={t(f.labelKey)}
                InputLabelProps={
                  f.type === 'date' ? { shrink: true } : undefined
                }
                value={popover.values[f.field] ?? ''}
                onChange={(e) =>
                  setPopover((p) =>
                    p
                      ? {
                          ...p,
                          values: { ...p.values, [f.field]: e.target.value },
                        }
                      : p,
                  )
                }
              >
                {f.type === 'select' &&
                  f.options?.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {t(o.labelKey)}
                    </MenuItem>
                  ))}
              </TextField>
            ))}
            <Button
              size="small"
              variant="contained"
              disabled={
                loadingFacet === popover.facet ||
                popover.fields.some((f) => !popover.values[f.field]?.trim())
              }
              onClick={() =>
                fire(popover.facet, popover.anchor, popover.values)
              }
            >
              {t('copilot.travelIntelligence.bar.consult')}
            </Button>
          </Box>
        )}
      </Popover>
    </>
  );
}
